import { CategoryButton } from '@/components/feature/floor-guide';
import type { KioskSelectedCategory } from '@/services/dto';
import { filterCategoriesWithMapPoi } from '@/utils/floor-guide';
import type { GroupedPoiByBuildingId } from '@/types/map';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { useAccessibilityTTS } from '@/hooks';
import { NAV_KEYS } from '@/constants/keyboard';
import { useConfigStore } from '@/stores/configStore';

type Props = {
    onSelectCategory?: (category: KioskSelectedCategory) => void;
    groupedPoiByBuildingId: GroupedPoiByBuildingId;
    buildingId?: string;
    isShowCategoryList?: boolean;
    selectedCategoryCode?: string;
};

export const VerticalCategoryList = ({
    onSelectCategory,
    groupedPoiByBuildingId,
    buildingId,
    isShowCategoryList,
    selectedCategoryCode,
}: Props) => {
    const { t, i18n } = useTranslation();
    const language = useMemo(() => i18n.language, [i18n]);
    const { speak } = useAccessibilityTTS();
    const listRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const selectedCategories = useConfigStore(
        (state) => state.config.server?.buildingMenus?.[language]?.find((menu) => menu.menuType === 'FLOOR')?.selectedCategories
    );
    const buildingCategories = useMemo(
        () => filterCategoriesWithMapPoi(selectedCategories, groupedPoiByBuildingId, buildingId, buildings),
        [selectedCategories, groupedPoiByBuildingId, buildingId, buildings]
    );

    const checkOverflow = useCallback(() => {
        if (listRef.current) {
            const { scrollWidth, clientWidth } = listRef.current;
            setIsOverflowing(scrollWidth > clientWidth);
        }
    }, []);

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [checkOverflow, buildingCategories]);

    return (
        <section className={clsx(styles['vertical-category-wrapper'])}>
            <div
                ref={listRef}
                className={clsx(styles['vertical-category__list'], 'scrollbar-hide', {
                    'justify-center': !isOverflowing,
                    'justify-start px-[calc(30*var(--vh-unit))]': isOverflowing,
                })}
                data-region-nav={isShowCategoryList}
                data-region-order={5}
                data-tts-scope="floor-button"
            >
                {Array.isArray(buildingCategories) &&
                    buildingCategories?.length > 0 &&
                    buildingCategories.map((item, index) => (
                        <div key={`${item.clientCode}-${index}`} className="shrink-0">
                            <CategoryButton
                                isSelected={selectedCategoryCode === item.categoryCode}
                                onClick={() => onSelectCategory?.(item)}
                                tabIndex={isShowCategoryList ? 7 : -1}
                                data-interactive
                                data-playsound="ting"
                                onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                    if (NAV_KEYS.includes(e.key)) {
                                        speak(
                                            t('floor_guide.text_to_speech.category_btn', {
                                                categoryName: item?.name || '',
                                            })
                                        );
                                    }
                                }}
                            >
                                {item?.name || ''}
                            </CategoryButton>
                        </div>
                    ))}
            </div>
        </section>
    );
};
